import React, { useEffect, useState } from "react";
import { Monitor, Tablet } from "lucide-react";

// Toggles between "PC" and "Tablet" viewport modes.
//   • PC mode:     forces `min-width: 1600px` on the app root so the whole
//                  desktop UI stays intact on tablets — user gets horizontal
//                  scroll + pinch-zoom to read details.
//   • Tablet mode: sidebar hides, wrappers collapse to `100%` width so the
//                  content flows in the available viewport (best on
//                  8"–10" tablets and phones).
// The preference is stored in localStorage so it persists across sessions
// and sticks per-device (a shared login on a warehouse tablet stays in
// tablet mode automatically).
const STORAGE_KEY = "crm-viewport-mode";

function detectDefault() {
  if (typeof window === "undefined") return "pc";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "pc" || stored === "tablet") return stored;
  // First visit: auto-pick based on viewport width. Anything under 1200px
  // (typical tablet portrait mode) starts in tablet layout.
  return window.innerWidth < 1200 ? "tablet" : "pc";
}

export default function ViewportModeToggle() {
  const [mode, setMode] = useState(detectDefault);

  useEffect(() => {
    document.documentElement.classList.remove("viewport-mode-pc", "viewport-mode-tablet");
    document.documentElement.classList.add(`viewport-mode-${mode}`);
    try { window.localStorage.setItem(STORAGE_KEY, mode); } catch (_) {}
  }, [mode]);

  const isPc = mode === "pc";
  const other = isPc ? "tablet" : "pc";
  const label = isPc ? "Tablet View" : "PC View";
  const Icon = isPc ? Tablet : Monitor;

  return (
    <button
      type="button"
      onClick={() => setMode(other)}
      data-testid="viewport-mode-toggle"
      title={`Switch to ${label} — currently ${mode.toUpperCase()}`}
      className="flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-400 text-xs font-mono transition-colors"
    >
      <Icon size={14} />
      <span className="uppercase tracking-widest">{label}</span>
    </button>
  );
}
